export enum BreakpointMinSizes {
    "xs" = "xs",
    "sm" = "sm",
    "md" = "md",
    "lg" = "lg",
    "xl" = "xl"
}

export enum BreakpointMaxSizes {
    "xxs" = "xxs",
    "xs" = "xs",
    "sm" = "sm",
    "md" = "md",
    "lg" = "lg"
}

export enum DeviceType {
    MOBILE = "MOBILE",
    DESKTOP = "DESKTOP"
}

type BreakPointValueObject = {
    min: Record<BreakpointMinSizes, number>;
    max: Record<BreakpointMaxSizes, number>;
};

type BreakPointObject = {
    min: Record<BreakpointMinSizes, string>;
    max: Record<BreakpointMaxSizes, string>;
};

const xs = 360,
    sm = 480,
    md = 768,
    lg = 992,
    xl = 1200;

const breakpointValues: BreakPointValueObject = {
    min: { xs, sm, md, lg, xl },
    max: {
        xxs: xs - 1,
        xs: sm - 1,
        sm: md - 1,
        md: lg - 1,
        lg: xl - 1
    }
};

const breakpoints: BreakPointObject = {
    min: {
        xs: `(min-width: ${breakpointValues.min.xs}px)`,
        sm: `(min-width: ${breakpointValues.min.sm}px)`,
        md: `(min-width: ${breakpointValues.min.md}px)`,
        lg: `(min-width: ${breakpointValues.min.lg}px)`,
        xl: `(min-width: ${breakpointValues.min.xl}px)`
    },
    max: {
        xxs: `(max-width: ${breakpointValues.max.xxs}px)`,
        xs: `(max-width: ${breakpointValues.max.xs}px)`,
        sm: `(max-width: ${breakpointValues.max.sm}px)`,
        md: `(max-width: ${breakpointValues.max.md}px)`,
        lg: `(max-width: ${breakpointValues.max.lg}px)`
    }
};

const sortedDeviceTypeMinBreakpointsSizes: Array<{
    device: DeviceType;
    breakpoint: BreakpointMinSizes | null;
}> = [
    { device: DeviceType.DESKTOP, breakpoint: BreakpointMinSizes.md },
    { device: DeviceType.MOBILE, breakpoint: null }
];

/**
 * Define the breakpoints class
 * @class Index
 */
export default class BreakpointsService {
    /**
     * Check if the viewport has the size of maximum small mobile device
     *
     * @returns Boolean
     */
    static matchesMaxXxs(): boolean {
        return window.matchMedia(breakpoints.max.xxs).matches;
    }

    /**
     * Check if the viewport has the size of maximum mobile device
     *
     * @returns Boolean
     */
    static matchesMaxXs(): boolean {
        return window.matchMedia(breakpoints.max.xs).matches;
    }

    /**
     * Check if the viewport has the size of maximum tablet device
     *
     * @returns Boolean
     */
    static matchesMaxSm(): boolean {
        return window.matchMedia(breakpoints.max.sm).matches;
    }

    /**
     * Check if the viewport has the size of maximum tablet device
     *
     * @returns Boolean
     */
    static matchesMaxMd(): boolean {
        return window.matchMedia(breakpoints.max.md).matches;
    }

    /**
     * Check if the viewport has the size of minimum tablet device
     *
     * @returns Boolean
     */
    static matchesMinXs(): boolean {
        return window.matchMedia(breakpoints.min.xs).matches;
    }

    /**
     * Check if the viewport has the size of minimum tablet device
     *
     * @returns Boolean
     */
    static matchesMinSm(): boolean {
        return window.matchMedia(breakpoints.min.sm).matches;
    }

    /**
     * Check if the viewport has the size of minimum desktop device
     *
     * @returns Boolean
     */
    static matchesMinMd(): boolean {
        return window.matchMedia(breakpoints.min.md).matches;
    }

    /**
     * Check if the viewport has the size of minimum large desktop device
     *
     * @returns Boolean
     */
    static matchesMinLg(): boolean {
        return window.matchMedia(breakpoints.min.lg).matches;
    }

    /**
     * Check if the viewport has the size of minimum extra large desktop device
     *
     * @returns Boolean
     */
    static matchesMinXl(): boolean {
        return window.matchMedia(breakpoints.min.xl).matches;
    }

    /**
     * Get the breakpoints object
     *
     * @returns breakpoints
     */
    static getBreakpointsObject(): BreakPointObject {
        return breakpoints;
    }

    /**
     * Get the breakpoint values object
     *
     * @returns breakpointValues
     */
    static getBreakpointValuesObject(): BreakPointValueObject {
        return breakpointValues;
    }

    /**
     * Get the breakpoint min values
     *
     * @param size
     * @returns breakpointValues
     */
    static getBreakpointMinValues(size: BreakpointMinSizes): number {
        return breakpointValues.min[size];
    }

    /**
     * Get the breakpoint max values
     *
     * @param size
     * @returns breakpointValues
     */
    static getBreakpointMaxValues(size: BreakpointMaxSizes): number {
        return breakpointValues.max[size];
    }

    static getDevice(): DeviceType {
        return (
            sortedDeviceTypeMinBreakpointsSizes.find(({ breakpoint }) =>
                breakpoint
                    ? window.matchMedia(breakpoints.min[breakpoint]).matches
                    : true
            )?.device ?? DeviceType.MOBILE
        );
    }
}
